import React, { useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { TabularIcon, ImagingClassificationIcon, ImagingSegmentationIcon } from "../../../components/TaskTypeIcons";
import HubIcon from "@mui/icons-material/Hub";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CircleIcon from "@mui/icons-material/Circle";
import LockIcon from "@mui/icons-material/Lock";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AdjustIcon from "@mui/icons-material/Adjust";
import RuleIcon from "@mui/icons-material/Rule";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import { tokens } from "../../../theme";
import { useAppContext } from "../../../contexts/AppContext";
import { resolveSiteGeo } from "../../../data/siteGeo";
import {
  mockDatasetDefinitions,
  capabilityStatusFromReadiness,
} from "../../../data/mockDatasetDefinitions";

/**
 * Prototype Option C — grouped /datasets card.
 *
 * Non-expanded layout (top to bottom):
 *   Header           — display_name + dataset_ref + task badge(s) top-right
 *   Description
 *   Task chips
 *   ┃ TARGET                     (cyan accent block)
 *   ┃ EXPECTED SCHEMA            (slate accent block, monospace value)
 *   ┃ COMPATIBLE MODELS          (violet accent block, chips + caveat footnote)
 *   ┃ SITE-LOCAL CAPABILITIES    (blue accent block, site chip strip)
 *   Validation note ⓘ
 *   Show/Hide site capabilities toggle
 *
 * Each of the 4 content sections is a colored block with its own accent bar
 * + tinted background + colored section label. This gives 4 clear vertical
 * focal points without burying counts behind summary tiles.
 *
 * No explicit count line is printed — the chip lists in Compatible Models
 * and Site-local Capabilities are themselves the count. Data unit is no
 * longer rendered (its metadata field remains in mockDatasetDefinitions
 * for future use).
 *
 * Product framing preserved:
 *   - Expected schema is a *requirement*, not a validation verdict.
 *   - Compatible models lists architectures structurally appropriate for
 *     the task; "default" marks a starting point, never a recommendation.
 *   - Per-site state lives in the expander as Available / Warming up /
 *     Needs review / Unavailable, not Pass / Fail.
 *   - No standalone "Imaging" label anywhere — taxonomy keys off
 *     `tasks_supported` via TASK_LABEL_FULL.
 */

const STATUS_DOT = {
  online: "#0087B4",
  offline: "var(--fx-text-muted)",
  maintenance: "#B08D57",
  unknown: "var(--fx-border-strong)",
};

const STATUS_TONE_STYLE = {
  ok:      { bg: "var(--fx-success-soft)", fg: "var(--fx-success-text)" },
  info:    { bg: "var(--fx-info-soft)",    fg: "var(--fx-info-text)" },
  warn:    { bg: "var(--fx-warning-soft)", fg: "var(--fx-warning-text)" },
  neutral: { bg: "var(--fx-subtle)",       fg: "var(--fx-text-secondary)" },
};

const ACCESS_SCOPE_STYLE = {
  self:         { bg: "var(--fx-info-soft)",        fg: "var(--fx-info-text)" },
  organization: { bg: "var(--fx-cat-indigo-soft)",  fg: "var(--fx-cat-indigo)" },
  denied:       { bg: "var(--fx-danger-soft)",      fg: "var(--fx-danger-text)" },
};

/** Canonical task taxonomy. No standalone "Imaging" anywhere. */
const TASK_LABEL_FULL = {
  tabular: "Tabular",
  imaging_classification: "Imaging · Classification",
  imaging_segmentation: "Imaging · Segmentation",
};

const TASK_ICON_COMPONENT = {
  tabular: TabularIcon,
  imaging_classification: ImagingClassificationIcon,
  imaging_segmentation: ImagingSegmentationIcon,
};

/**
 * Per-task color family. Picks three cool / neutral hues so the eye can
 * identify dataset type at a glance, without any warning or error
 * connotation that amber / red would carry:
 *   - Tabular                  → indigo (deep blue-purple, "structured rows")
 *   - Imaging · Classification → emerald (cool green, "label / decision")
 *   - Imaging · Segmentation   → teal    (cool blue-green, "mask / outline")
 *
 * Each family provides: a `chip` styling for the header task badges, a
 * single `title` color for the dataset display_name, and a `pill` styling
 * (lighter shade of the same family) for the dataset_ref version tag.
 *
 * Section block colors (Target/Schema/Models/Sites cyan/stone/violet/blue)
 * are intentionally NOT swapped per family — those are a stable section
 * vocabulary that applies to every card. The task family colors only drive
 * the three header elements (chip, title, version pill).
 */
const TASK_FAMILY_STYLE = {
  tabular: {
    // Indigo — deep blue-purple, calm, "structured". Soft alpha tint + mode-aware
    // text token so it reads correctly on light AND dark cards.
    chip:  { bg: "var(--fx-cat-indigo-soft)", text: "var(--fx-cat-indigo)", border: "var(--fx-cat-indigo-soft)" },
    title: "var(--fx-cat-indigo)",                                 // mode-aware indigo
    pill:  { bg: "var(--fx-cat-indigo-soft)", text: "var(--fx-cat-indigo)" },
  },
  imaging_classification: {
    // Emerald — cool green
    chip:  { bg: "var(--fx-cat-emerald-soft)", text: "var(--fx-cat-emerald)", border: "var(--fx-cat-emerald-soft)" },
    title: "var(--fx-cat-emerald)",                                // mode-aware emerald
    pill:  { bg: "var(--fx-cat-emerald-soft)", text: "var(--fx-cat-emerald)" },
  },
  imaging_segmentation: {
    // Teal — cool blue-green.
    chip:  { bg: "var(--fx-cat-teal-soft)", text: "var(--fx-cat-teal)", border: "var(--fx-cat-teal-soft)" },
    title: "var(--fx-cat-teal)",                                   // mode-aware teal
    pill:  { bg: "var(--fx-cat-teal-soft)", text: "var(--fx-cat-teal)" },
  },
};

/** Fallback when a card has no recognised task in tasks_supported. */
const TASK_FAMILY_FALLBACK = {
  chip:  { bg: "var(--fx-info-soft)", text: "var(--fx-info-text)", border: "var(--fx-info-soft)" },
  title: "var(--fx-text-strong)",
  pill:  { bg: "var(--fx-subtle)", text: "var(--fx-text-secondary)" },
};

/**
 * Section accent tokens — retained only because the popover header still
 * uses the "sites" accent for its capability chip. The old four coloured
 * section blocks inside the card were replaced with a unified metadata
 * grid for a cleaner, more professional look (no per-section chrome).
 */
const SECTION_TOKENS = {
  sites: {
    accent: "var(--fx-info-text)",
    bg:     "var(--fx-info-soft)",
    border: "var(--fx-info-border)",
  },
};

/**
 * Single row in the new metadata grid. A small uppercase label on the left
 * and the value (or chip strip) on the right, separated by a thin dashed
 * divider beneath it. Keeps the four pieces of metadata (Target, Schema,
 * Models, Sites) visually equivalent instead of giving each its own colour.
 */
const MetaRow = ({ label, tooltip, children, isLast }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "104px 1fr" },
      gap: { xs: 0.25, sm: 1.5 },
      alignItems: "flex-start",
      py: 0.9,
      borderBottom: isLast ? "none" : "1px dashed var(--fx-border)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, pt: 0.2 }}>
      <Typography
        sx={{
          color: "var(--fx-text-secondary)",
          fontWeight: 700,
          fontSize: "0.625rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} placement="top-start">
          <InfoOutlinedIcon
            sx={{ fontSize: 11, color: "var(--fx-text-muted)", cursor: "help" }}
          />
        </Tooltip>
      )}
    </Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

/** Format `sample_count_estimate` (number | string | undefined) into a
 *  reader-friendly value for the expanded row's "Sample estimate" field.
 *  - number → "~12,345 records"
 *  - string like "11000+" → "11,000+ records" (commas inserted into any
 *    numeric run; trailing qualifiers like "+" are preserved)
 *  - empty / undefined → "—" */
const formatSampleEstimate = (v) => {
  if (v === undefined || v === null || v === "") return "None";
  if (typeof v === "number") return `~${v.toLocaleString()} records`;
  const formatted = String(v).replace(/\d+/g, (n) => Number(n).toLocaleString());
  return `${formatted} records`;
};

/** Caption-style key label for the expanded row's metadata grid. */
const MetaLabel = ({ colors, children }) => (
  <Typography
    variant="caption"
    sx={{
      color: colors.grey[500],
      fontWeight: 700,
      fontSize: "0.6rem",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

const DatasetGroupCard = ({ group }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [expanded, setExpanded] = useState(false);
  const popoverAnchorRef = useRef(null);
  const { sites } = useAppContext();

  const lookupSite = (siteId) => (sites || []).find((s) => s.site_id === siteId);

  const def = mockDatasetDefinitions[group.dataset_ref];
  const displayName = def?.display_name || group.display_name || group.dataset_ref;

  // Canonical task list: derived directly from tasks_supported. Unknown task
  // ids are filtered. No standalone "Imaging" can appear here by construction.
  const taskKeys = (group.tasks_supported || []).filter((t) => TASK_LABEL_FULL[t]);

  // Primary task family drives the dataset title color and version pill
  // colors. For multi-task cards (rare) we pick the first known task; the
  // header still shows one chip per task with each chip in its own family
  // color so the dual-task case stays visually unambiguous.
  const primaryFamily = TASK_FAMILY_STYLE[taskKeys[0]] || TASK_FAMILY_FALLBACK;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--fx-paper)",
        border: "1px solid var(--fx-border)",
        borderRadius: 2,
        boxShadow: "var(--fx-shadow-sm)",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: "var(--fx-border-strong)",
          boxShadow: "var(--fx-shadow-lg)",
        },
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2.25 }}>
        {/* Header: display_name + dataset_ref + top-right task badge(s) */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ pr: 1, flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: "var(--fx-text-primary)",
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: "1rem",
                letterSpacing: "-0.005em",
              }}
            >
              {displayName}
            </Typography>
            <Tooltip title="Dataset reference slug · trailing token (e.g. v1) is the schema version. All participating sites in this card host the same logical dataset under this ref.">
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  mt: 0.5,
                  px: 0.75,
                  py: 0.15,
                  bgcolor: "transparent",
                  color: "var(--fx-text-secondary)",
                  border: "1px solid var(--fx-border-strong)",
                  borderRadius: "5px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: "0.6875rem",
                  fontWeight: 500,
                  lineHeight: 1.4,
                  wordBreak: "break-all",
                  cursor: "help",
                }}
              >
                {group.dataset_ref}
              </Box>
            </Tooltip>
          </Box>

          {/* Task badges — one chip per task, each in its own family colour
              (Tabular = amber, Imaging · Classification = emerald,
              Imaging · Segmentation = rose). Never standalone "Imaging". */}
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="flex-end"
            flexWrap="wrap"
            gap={0.5}
            sx={{ flexShrink: 0, maxWidth: "55%" }}
          >
            {taskKeys.map((t) => {
              const Icon = TASK_ICON_COMPONENT[t];
              const f = TASK_FAMILY_STYLE[t] || TASK_FAMILY_FALLBACK;
              return (
                <Chip
                  key={t}
                  size="small"
                  icon={Icon ? <Icon size={13} /> : undefined}
                  label={TASK_LABEL_FULL[t]}
                  sx={{
                    bgcolor: f.chip.bg,
                    color: f.chip.text,
                    fontWeight: 700,
                    fontSize: "0.6875rem",
                    borderRadius: "999px",
                    height: 22,
                    border: `1px solid ${f.chip.border}`,
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>

        {/* Description */}
        {def?.description && (
          <Typography
            sx={{
              color: "var(--fx-text-secondary)",
              fontSize: "0.8125rem",
              lineHeight: 1.5,
              mt: 1.25,
            }}
          >
            {def.description}
          </Typography>
        )}

        {/* ── Unified metadata grid (replaces the 4 coloured section blocks) ──
            Single light surface, dashed dividers between rows. Keeps the
            full information density but removes the visual noise of four
            separate tinted sub-cards. Title / version pill / task chips in
            the header still carry the task-family colour, so the card
            never loses its task identity — it just stops shouting. */}
        <Box
          sx={{
            mt: 2,
            px: 1.75,
            py: 0.5,
            bgcolor: "var(--surface-inset)",
            border: "1px solid var(--fx-border)",
            borderRadius: 2,
          }}
        >
          {/* TARGET */}
          {def?.target_variable && (
            <MetaRow label="Target">
              <Typography
                sx={{
                  color: "var(--fx-text-strong)",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                }}
              >
                {def.target_variable}
              </Typography>
            </MetaRow>
          )}

          {/* SCHEMA */}
          <MetaRow
            label="Schema"
            tooltip="Schema requirement this dataset places on each participating site. Per-site compatibility is checked at run validation time."
          >
            <Typography
              sx={{
                color: "var(--fx-text-strong)",
                fontWeight: 500,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.75rem",
                lineHeight: 1.5,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {def?.expected_schema || "Not set"}
            </Typography>
          </MetaRow>

          {/* MODELS */}
          {def?.compatible_models?.length > 0 && (
            <MetaRow
              label="Models"
              tooltip="Architectures structurally suited to this dataset's task. Compatibility, not a performance ranking."
            >
              <Stack direction="row" gap={0.5} flexWrap="wrap">
                {def.compatible_models.map((m) => (
                  <Chip
                    key={m}
                    size="small"
                    label={m}
                    sx={{
                      bgcolor: "var(--fx-paper)",
                      color: "var(--fx-text-primary)",
                      fontWeight: 600,
                      fontSize: "0.6875rem",
                      height: 20,
                      border: "1px solid var(--fx-border-strong)",
                      "& .MuiChip-label": { px: 0.85 },
                    }}
                  />
                ))}
              </Stack>
            </MetaRow>
          )}

          {/* SITES */}
          <MetaRow
            label="Sites"
            tooltip="Sites that host a capability for this dataset reference. Click 'Show capabilities' for per-site details."
            isLast
          >
            {group.capabilities.length === 0 ? (
              <Typography sx={{ color: colors.grey[500], fontSize: "0.75rem" }}>None</Typography>
            ) : (
              <Stack direction="row" gap={0.5} flexWrap="wrap">
                {group.capabilities.map((cap) => {
                  const site = lookupSite(cap.site_id);
                  const denied = site?.access_scope === "denied";
                  return (
                    <Tooltip
                      key={cap.capability_id}
                      title={`${site?.display_name || cap.site_id}${denied ? " · access required" : ""}`}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                          bgcolor: denied ? "var(--fx-danger-soft)" : "var(--fx-paper)",
                          color: denied ? "var(--fx-danger-text)" : "var(--fx-text-secondary)",
                          border: `1px solid ${denied ? "var(--fx-danger-border)" : "var(--fx-border-strong)"}`,
                          px: 0.75,
                          py: 0.2,
                          borderRadius: 999,
                          opacity: site?.status === "online" ? 1 : 0.7,
                        }}
                      >
                        <CircleIcon
                          sx={{
                            fontSize: 7,
                            color: STATUS_DOT[site?.status] || STATUS_DOT.unknown,
                          }}
                        />
                        <Typography sx={{ fontWeight: 600, fontSize: "0.6875rem", lineHeight: 1 }}>
                          {site?.display_name || cap.site_id}
                        </Typography>
                        {denied && <LockIcon sx={{ fontSize: 9 }} />}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            )}
          </MetaRow>
        </Box>

        {/* Expand toggle — anchors a popover so opening it does NOT reflow the grid row */}
        <Box sx={{ mt: "auto", pt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
          <Typography sx={{ color: colors.grey[500], fontSize: "0.7rem", fontWeight: 500 }}>
            {group.capabilities.length} {group.capabilities.length === 1 ? "site" : "sites"}
          </Typography>
          <Box
            ref={popoverAnchorRef}
            component="button"
            type="button"
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              border: `1px solid ${expanded ? colors.primary[400] : "var(--fx-border)"}`,
              bgcolor: expanded ? "rgba(27, 54, 93, 0.04)" : "var(--fx-paper)",
              borderRadius: 999,
              px: 1.5,
              py: 0.5,
              cursor: "pointer",
              fontFamily: "inherit",
              color: 'var(--fx-text-primary)',
              fontSize: "0.75rem",
              fontWeight: 600,
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: "rgba(27, 54, 93, 0.06)", borderColor: colors.primary[400] },
            }}
          >
            {expanded ? "Hide site capabilities" : "Show site capabilities"}
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transition: "transform 0.2s ease",
                transform: expanded ? "rotate(180deg)" : "rotate(0)",
              }}
            />
          </Box>
        </Box>

        {/* Popover: floating panel with the per-site capability detail. Anchored
            to the toggle button so opening it never resizes the card or
            reflows neighboring cards in the grid row. */}
        <Popover
          open={expanded}
          anchorEl={popoverAnchorRef.current}
          onClose={() => setExpanded(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                width: { xs: "min(94vw, 460px)", sm: 480 },
                maxHeight: 560,
                overflow: "auto",
                borderRadius: 2,
                border: "1px solid var(--fx-border)",
                boxShadow: "var(--fx-shadow-xl)",
              },
            },
          }}
        >
          <Box sx={{ position: "sticky", top: 0, bgcolor: "var(--fx-paper)", zIndex: 1, px: 2, py: 1.25, borderBottom: "1px solid var(--fx-border)" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, color: colors.grey[500], textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Site capabilities
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: 'var(--fx-text-primary)', fontWeight: 600, lineHeight: 1.3, mt: 0.25 }}>
                  {displayName}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={`${group.capabilities.length} ${group.capabilities.length === 1 ? "site" : "sites"}`}
                sx={{ bgcolor: SECTION_TOKENS.sites.bg, color: SECTION_TOKENS.sites.accent, fontWeight: 700, fontSize: "0.625rem", border: `1px solid ${SECTION_TOKENS.sites.border}` }}
              />
            </Stack>
          </Box>
          <Box sx={{ p: 1.5 }}>
            <Stack spacing={1.25}>
              {group.capabilities.map((cap) => {
                const site = lookupSite(cap.site_id);
                const denied = site?.access_scope === "denied";
                const capStatus = capabilityStatusFromReadiness(cap.readiness_status);
                const statusStyle = STATUS_TONE_STYLE[capStatus.tone] || STATUS_TONE_STYLE.neutral;
                const accessStyle = ACCESS_SCOPE_STYLE[site?.access_scope] || ACCESS_SCOPE_STYLE.organization;
                const taskLabels = (cap.tasks_supported || []).filter((t) => TASK_LABEL_FULL[t]);
                return (
                  <Box
                    key={cap.capability_id}
                    sx={{
                      px: 1.75,
                      py: 1.5,
                      bgcolor: "var(--fx-subtle)",
                      border: "1px solid var(--fx-border)",
                      borderRadius: 2,
                    }}
                  >
                    {/* ── Top row: Site / Org (left) · Status / Access (right) ── */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "flex-start" }}
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <HubIcon sx={{ fontSize: 16, color: colors.secondary[500] }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "var(--fx-text-strong)" }}
                            noWrap
                          >
                            {site?.display_name || cap.site_id}
                          </Typography>
                          <Tooltip title={`Site status: ${site?.status || "unknown"}`}>
                            <CircleIcon
                              sx={{
                                fontSize: 8,
                                color: STATUS_DOT[site?.status] || STATUS_DOT.unknown,
                              }}
                            />
                          </Tooltip>
                        </Stack>
                        {(() => {
                          const city = resolveSiteGeo(site)?.city;
                          const locationLabel = [city, site?.organization_display_name]
                            .filter(Boolean)
                            .join(" · ");
                          return locationLabel ? (
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.4}
                              sx={{ ml: 2.3, mt: 0.15 }}
                            >
                              <LocationOnIcon
                                sx={{ fontSize: 13, color: colors.grey[500], flexShrink: 0 }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.grey[500],
                                  fontSize: "0.72rem",
                                  lineHeight: 1.3,
                                }}
                              >
                                {locationLabel}
                              </Typography>
                            </Stack>
                          ) : null;
                        })()}
                      </Box>

                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Tooltip title="Capability availability reported by the site. Not a run-time validation outcome.">
                          <Chip
                            size="small"
                            label={capStatus.label}
                            sx={{
                              bgcolor: statusStyle.bg,
                              color: statusStyle.fg,
                              fontWeight: 700,
                              fontSize: "0.62rem",
                              height: 22,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          />
                        </Tooltip>
                        {denied ? (
                          <Tooltip title="Access required - contact your administrator">
                            <Chip
                              size="small"
                              icon={<LockIcon sx={{ fontSize: "12px !important" }} />}
                              label="denied"
                              sx={{
                                bgcolor: ACCESS_SCOPE_STYLE.denied.bg,
                                color: ACCESS_SCOPE_STYLE.denied.fg,
                                fontWeight: 700,
                                fontSize: "0.62rem",
                                height: 22,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                "& .MuiChip-icon": { color: "inherit" },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label={site?.access_scope || "Unknown"}
                            sx={{
                              bgcolor: accessStyle.bg,
                              color: accessStyle.fg,
                              fontWeight: 700,
                              fontSize: "0.62rem",
                              height: 22,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>

                    {/* ── Divider ──────────────────────────────────────── */}
                    <Box sx={{ borderTop: "1px dashed var(--fx-border)", my: 1.25 }} />

                    {/* ── Metadata key/value block ─────────────────────── */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "120px 1fr" },
                        rowGap: 0.65,
                        columnGap: 1.25,
                        alignItems: "center",
                      }}
                    >
                      <MetaLabel colors={colors}>Capability ID</MetaLabel>
                      <Tooltip title={cap.capability_id} placement="top-start">
                        <Typography
                          sx={{
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            fontSize: "0.74rem",
                            color: "var(--fx-text-strong)",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cap.capability_id}
                        </Typography>
                      </Tooltip>

                      <MetaLabel colors={colors}>Dataset ref</MetaLabel>
                      <Box>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: 0.75,
                            py: 0.2,
                            bgcolor: "var(--fx-paper)",
                            border: "1px solid var(--fx-border)",
                            borderRadius: 0.75,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            color: "var(--fx-text-secondary)",
                            lineHeight: 1.5,
                            wordBreak: "break-all",
                          }}
                        >
                          {cap.dataset_ref}
                        </Box>
                      </Box>

                      <MetaLabel colors={colors}>Tasks</MetaLabel>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {taskLabels.length === 0 ? (
                          <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                            None
                          </Typography>
                        ) : (
                          taskLabels.map((t) => {
                            const Icon = TASK_ICON_COMPONENT[t];
                            const f = TASK_FAMILY_STYLE[t] || TASK_FAMILY_FALLBACK;
                            return (
                              <Chip
                                key={`${cap.capability_id}-${t}`}
                                size="small"
                                icon={
                                  Icon ? <Icon size={13} /> : undefined
                                }
                                label={TASK_LABEL_FULL[t]}
                                sx={{
                                  bgcolor: f.chip.bg,
                                  color: f.chip.text,
                                  fontWeight: 600,
                                  fontSize: "0.62rem",
                                  height: 20,
                                  border: `1px solid ${f.chip.border}`,
                                  "& .MuiChip-icon": { color: "inherit" },
                                }}
                              />
                            );
                          })
                        )}
                      </Stack>

                      <MetaLabel colors={colors}>Sample estimate</MetaLabel>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          color: "var(--fx-text-strong)",
                          fontWeight: 600,
                        }}
                      >
                        {formatSampleEstimate(cap.sample_count_estimate)}
                      </Typography>
                    </Box>

                    {/* ── Site admin note — HIDDEN ─────────────────────────
                       *
                       * Free-text capability annotation from backend's
                       * `cap.notes`. Hidden behind `false &&` because the
                       * current backend content overlaps with schema
                       * validation findings (rendered on /configs
                       * SchemaPreCheckPanel + /execute Federated Data Review
                       * card) and was confusing users.
                       *
                       * To re-enable: drop the `false &&` guard below.
                       * Future use is reserved for true editorial/operational
                       * info (maintenance windows, contact info, changelog),
                       * not validator-style messages.
                       * ───────────────────────────────────────────────────── */}
                    {false && cap.notes && (
                      <Box
                        sx={{
                          mt: 1.25,
                          px: 1,
                          py: 0.75,
                          bgcolor: "var(--fx-subtle)",
                          border: "1px solid var(--fx-border)",
                          borderRadius: 0.75,
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: "var(--fx-text-secondary)", mt: "1px" }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "var(--fx-text-secondary)",
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                lineHeight: 1.2,
                              }}
                            >
                              Site admin note
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "var(--fx-text-secondary)",
                                fontSize: "0.72rem",
                                fontStyle: "italic",
                                lineHeight: 1.4,
                                mt: 0.25,
                              }}
                            >
                              {cap.notes}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Popover>
      </CardContent>
    </Card>
  );
};

export default DatasetGroupCard;
